/**
 * The studio landing page, /.
 *
 * Pilates, yoga, spinning, gyms. Most of the copy lives in the sibling
 * content files and is gathered here; the short headings that used to be
 * written straight into the section components moved here with them.
 */
import { valueProps } from "../valueProps";
import { featureThemes } from "../features";
import { howItWorks } from "../howItWorks";
import { industries } from "../industries";
import { testimonials } from "../testimonials";
import { faq } from "../faq";
import { customers } from "../customers";
import devices from "@/assets/two_devices.png";

export const fitnessLanding = {
  id: "fitness",
  path: "/",

  hero: {
    eyebrow: "ניהול אימונים ומתאמנים לסטודיו שלך",
    // The slogan is unchanged — see the plan's decision log.
    slogan: ["אנחנו ננהל את העסק עבורכם", "אתם תעשו את העבודה הטובה ביותר"],
    lead: "אפלאטס מנהלת את לו״ז האימונים, קבוצות, מנויים, כרטיסיות, מדידות גוף והמתאמנות — באפליקציה שנושאת את השם, הלוגו והצבעים של הסטודיו שלכם.",
    proof: ["אפליקציה עם המותג של הסטודיו"],
    // The booked session the hero devices stand on.
    // The seats fill one by one in the hero, then the class reads full.
    slot: {
      time: "09:00 – 11:00",
      title: "פילאטיס מכשירים",
      seats: { of: 10, initials: ["נ", "ש", "מ", "ר", "ד", "א", "ל", "ת", "י", "ע"] },
      fullLabel: "מלא",
    },
    image: devices,
    imageAlt:
      "אפליקציית אפלאטס על שני מכשירים — לו״ז הסטודיו ומסך הרשמה לאימון",
  },

  // The band of classes under the hero. A sample day, not a customer's.
  ticker: {
    fullLabel: "מלא",
    rows: [
      [
        { time: "07:00", name: "ספינינג", taken: 12, of: 16 },
        { time: "08:30", name: "פילאטיס מזרן", taken: 8, of: 10 },
        { time: "09:00", name: "פילאטיס מכשירים", taken: 10, of: 10 },
        { time: "10:30", name: "יוגה", taken: 6, of: 12 },
        { time: "12:00", name: "TRX", taken: 7, of: 8 },
        { time: "16:00", name: "פילאטיס הריון", taken: 5, of: 8 },
      ],
      [
        { time: "17:30", name: "פילאטיס מכשירים", taken: 9, of: 10 },
        { time: "18:00", name: "יוגה", taken: 7, of: 10 },
        { time: "18:30", name: "אימון פונקציונלי", taken: 14, of: 14 },
        { time: "19:30", name: "פילאטיס מזרן", taken: 9, of: 10 },
        { time: "20:00", name: "ספינינג", taken: 11, of: 16 },
        { time: "20:30", name: "מתיחות", taken: 4, of: 10 },
      ],
    ],
  },

  logos: {
    label: "סטודיו שכבר עובדים עם אפלאטס",
    customers,
  },

  valueProps: {
    eyebrow: "מה תקבלו",
    title: "פחות ניהול, יותר מקצועיות",
    subtitle:
      "אפלאטס לוקחת מכם את ניהול הלו״ז והכרטיסיות ומעניקה לכם זמן להעשרת הידע, התפתחות מקצועית, יחס וליווי אישי למתאמנות, וכמובן זמן לחיים האישיים.",
    items: valueProps,
    // Sample data for the tile illustrations. Drawings of the app, not claims.
    art: {
      slots: [
        { time: "09:00", name: "פילאטיס מכשירים", taken: 10, of: 10 },
        { time: "18:00", name: "יוגה", taken: 7, of: 10 },
        { time: "19:30", name: "פילאטיס מזרן", taken: 9, of: 10 },
      ],
      fullLabel: "מלא",
      avatars: ["נ", "ש", "מ", "ר"],
      capacityChip: "10/10",
      toast: { title: "התפנה מקום", text: "יוגה · היום 18:00" },
    },
  },

  features: {
    eyebrow: "למה אפלאטס",
    title: "כל מה שהסטודיו צריך, במקום אחד",
    themes: featureThemes,
  },

  howItWorks: {
    eyebrow: "איך זה עובד",
    title: "שלושה צעדים, ואתם באוויר",
    subtitle:
      "אתם לא צריכים להבין במערכות. אנחנו מקימים את הסטודיו, אתם מאשרים.",
    steps: howItWorks,
    art: {
      formName: "דנה",
      setupItems: ["לו״ז שבועי", "סוגי אימונים", "קבוצות ומנויים", "לוגו וצבעים"],
      reminder: { title: "תזכורת", text: "האימון מתחיל בעוד שעה" },
      booked: { title: "נרשמת לאימון", text: "פילאטיס מכשירים · ד׳ 09:00" },
    },
  },

  industries: {
    items: industries,
    // The salons have their own page; the switcher points there.
    crossLink: { label: "מספרות, יופי וציפורניים", to: "/salons" },
  },

  testimonials: {
    title: "סטודיו שכבר עברו את זה",
    items: testimonials,
  },

  faq,

  lead: {
    title: "תנו לנו לנהל את הסטודיו, אתם תאמנו",
    text: "השאירו פרטים ונחזור אליכם עם הדגמה קצרה של המערכת על הסטודיו שלכם.",
    points: [
      "שיחה קצרה להבין איך הסטודיו עובד",
      "הקמה מלאה על ידינו — אתם לא נוגעים בכלום",
      "אפליקציה עם השם והלוגו שלכם",
      "ליווי שוטף בהטמעה וגם אחרי העלייה לאוויר",
    ],
    businessPlaceholder: "הסטודיו של ישראל",
  },

  footer: {
    tagline: "אנחנו ננהל את הסטודיו עבורכם, אתם תעשו את העבודה הטובה ביותר.",
    crossLink: { label: "למספרות ולסלוני יופי", to: "/salons" },
  },
};
